<template>
  <teleport to="body">
    <div @click.self="done(false)" v-if="isRevealed" class="core-modal-backdrop">
      <div class="core-modal-surface bg-white p-4 w-[480px] max-w-full border border-neutral-300">
        <div :class="modalContent.level" class="core-modal-content">
          <h2 class="mb-4 font-semibold text-xl text-neutral-900">{{ modalContent.title }}</h2>
          <p class="mb-6 text-base text-neutral-550">{{ modalContent.text }}</p>
          <div class="flex gap-2" v-if="modalContent.confirm">
            <button
              class="core-button-primary core-action-button core-action-button--primary h-11 flex-1"
              @click="done(true)">
              {{ modalContent.confirm }}
            </button>
            <button
              v-if="modalContent.cancel"
              @click="done(false)"
              class="core-button-supporting core-action-button core-action-button--supporting h-11 flex-1">
              {{ modalContent.cancel }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { DialogEvent, type ModalContent, useDialogBus } from '@/helpers/event-bus'
import { useI18n } from 'vue-i18n'
import { useClientStore } from '@/stores/client'
import { useRouter } from 'vue-router'

const { t } = useI18n()
const dialogBus = useDialogBus()
const router = useRouter()
const isRevealed = ref(false)
const clientStore = useClientStore()

let modalContent: ModalContent = {
  title: '',
  text: '',
  cancel: undefined,
  confirm: undefined,
}
onMounted(() => {
  dialogBus.on(DialogEvent.SHOW_CONFIRM, showConfirm)
  dialogBus.on(DialogEvent.SHOW_DIALOG, showDialog)
  dialogBus.on(DialogEvent.SHOW_ERROR, showError)
  dialogBus.on(DialogEvent.FORCE_QUITE, reset)
})

const showConfirm = function (data: ModalContent) {
  modalContent.title = data.title
  modalContent.text = data.text
  modalContent.confirm = data.confirm || t('general.button.confirm')
  modalContent.cancel = data.cancel || t('general.button.cancel')
  modalContent.level = data.level || 'info'
  isRevealed.value = true
}

const showDialog = function (data: ModalContent) {
  modalContent.title = data.title
  modalContent.text = data.text
  modalContent.confirm = data.confirm || t('general.button.ok')
  modalContent.cancel = undefined
  modalContent.level = data.level || 'info'
  isRevealed.value = true
}

const showError = function (data: ModalContent) {
  modalContent.title = data.title
  modalContent.text = data.text
  modalContent.confirm = data.confirm || undefined
  modalContent.cancel = data.cancel || undefined
  modalContent.level = data.level || 'error'
  modalContent.reload = data?.reload
  isRevealed.value = true
}

const done = function (result: boolean) {
  if (!result && modalContent.level === 'severe') {
    return // severe is not cancelable by backdrop click - severe Errors should not have a cancel button!
  }
  if (result && modalContent?.reload) {
    router.push({ name: 'reload', params: { path: clientStore?.clientFullPath } })
  } else {
    dialogBus.emit(DialogEvent.DONE, result)
  }
  reset()
}

const reset = function () {
  modalContent = {
    title: '',
    text: '',
    cancel: undefined,
    confirm: undefined,
    level: undefined,
    reload: false,
  }
  isRevealed.value = false
}
</script>

<style lang="postcss" scoped>
.core-modal-backdrop {
  @apply fixed inset-0 overflow-y-auto overflow-x-hidden;
  @apply z-70 flex items-center justify-center;
  @apply bg-neutral-100/15 backdrop-blur-[2px];

  .core-modal-content {
    &.severe,
    &.error {
      @apply p-4 border-4 border-red-700;
      h2 {
        @apply text-red-700;
      }
    }
  }
}

.core-modal-surface {
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.12),
    0 8px 16px -12px rgba(0, 0, 0, 0.2);
}
</style>
